'use client'

import { useRef } from 'react'
import { Transition } from '@wiggui/ds/Transition'
import Tooltip from '@wiggui/ds/Tooltip'
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md'
import { useLayoutV2Store } from '../../../store/layout-v2.store'
import { IntercomButton } from '../../../components/IntercomButton'
import { composeClasses } from '../../../../utils/src/strings'
import { ROUTES } from '../../../constants/routes'
import { useGetMenuOptions } from '../hooks/useGetMenuOptions'
import { MenuOption } from './MenuOption'
import { ButtonModalLogin } from './ButtonModalLogin'
import { ButtonProfile } from './ButtonProfile'
import './MenuOptions.css'
import HardLink from '../../../components/hard-link/HardLink'
import { IconWiggot } from '@wiggui/ds/IconWiggot'
import { NewIconBlackWiggot } from '../assets/NewIconBlackWiggot'

export const MenuOptions = ({ className }: { className?: string }) => {
  const {
    isExpandedMenu,
    setIsExpandedMenu,
    isEnableFixedMenu,
    setIsEnableFixedMenu
  } = useLayoutV2Store()

  const menuContainerRef = useRef<HTMLDivElement>(null)

  const { menuOptions } = useGetMenuOptions()

  const handleMouseLeave = (event: React.MouseEvent) => {
    const relatedTarget = event?.relatedTarget as Node | null
    if (
      menuContainerRef?.current &&
      relatedTarget &&
      relatedTarget instanceof Node &&
      !menuContainerRef?.current?.contains(relatedTarget)
    ) {
      setIsExpandedMenu(false)
    }
  }

  const handleTextIconFixed = isEnableFixedMenu
    ? 'Contraer menú automáticamente'
    : 'Mantener menú expandido'

  return (
    <header
      ref={menuContainerRef}
      onMouseEnter={() => setIsExpandedMenu(true)}
      onMouseLeave={handleMouseLeave}
      className={composeClasses(
        className,
        'py-3 px-2 h-full ui-kit-scrollbar overflow-y-auto menu-header overflow-x-hidden hidden md:block',
        isExpandedMenu ? 'menu-header--expanded' : 'menu-header--collapsed'
      )}
    >
      <nav className="flex flex-col w-full h-full justify-between items-stretch">
        <div className="flex flex-col w-full h-full">
          <div className="flex flex-row w-full min-h-[68px] px-1.5 pt-3 pb-6 items-center justify-between gap-1">
            <HardLink
              href={ROUTES.HOME}
              className="w-full flex flex-row items-center justify-start gap-2"
            >
              <div className="min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]">
                <IconWiggot variant="black" height="24px" />
              </div>

              <Transition
                duration={300}
                show={isExpandedMenu}
                className="w-fit h-full flex items-center pt-1"
              >
                <NewIconBlackWiggot />
              </Transition>
            </HardLink>
            <Transition
              duration={300}
              show={isExpandedMenu}
              className="w-fit z-3"
            >
              <Tooltip
                content={handleTextIconFixed}
                placement="bottom"
                tooltipClassName="z-3"
              >
                <button
                  aria-label={handleTextIconFixed}
                  onClick={() => setIsEnableFixedMenu(!isEnableFixedMenu)}
                  type="button"
                  className={composeClasses(
                    'cursor-pointer rounded-xl min-w-[32px] min-h-[32px] max-w-[32px] flex items-center justify-center max-h-[32px] hover:bg-[#DEE5F1] active:bg-surface-gray-10 group transition-colors duration-300 ease-in-out'
                  )}
                >
                  {isEnableFixedMenu ? (
                    <MdPushPin className="min-w-[16px] min-h-[16px]" />
                  ) : (
                    <MdOutlinePushPin className="min-w-[16px] min-h-[16px]" />
                  )}
                </button>
              </Tooltip>
            </Transition>
          </div>
          <ul className="flex flex-col w-full min-h-fit mb-4">
            {menuOptions?.map((option) => {
              return <MenuOption key={option.id} option={option} />
            })}
          </ul>
        </div>
        <div className="flex flex-col min-h-[136px] items-start justify-center gap-2 w-full mt-auto">
          <ButtonModalLogin
            isShowText={isExpandedMenu}
            isShowBadge={isExpandedMenu}
          />
          <IntercomButton
            showText={isExpandedMenu}
            className="min-h-[40px] h-10 px-[10px]"
            iconClassName="min-w-[18px] min-h-[18px] max-h-[18px] max-w-[18px]"
          />
          <ButtonProfile isExpandedMenu={isExpandedMenu} />
        </div>
      </nav>
    </header>
  )
}

export default MenuOptions
